import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Crown, RefreshCw, Calendar, GraduationCap, Briefcase, HeartHandshake, Building2, CreditCard, DollarSign, Baby, Pencil } from "lucide-react";
import { EditCorporateDialog } from "@/components/platform/EditClientDialog";
import { OwnerSubscriptionCard } from "@/components/platform/OwnerSubscriptionCard";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function PlatformCorporateDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/platform/corporate/${id}/detail`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load"))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(load, [load]);

  if (loading && !data) {
    return <main className="px-6 md:px-10 py-8 text-slate-500" data-testid="corporate-detail-loading">Loading…</main>;
  }
  if (!data) {
    return <main className="px-6 md:px-10 py-8 text-slate-500">Corporate account not found.</main>;
  }

  const k = data.corporate;
  const locStatus = (c) => {
    if (c.deactivated) return { label: "Deactivated", cls: "bg-slate-200 text-slate-700" };
    const now = new Date();
    if (c.paid_until && new Date(c.paid_until) > now) return { label: "Active", cls: "bg-emerald-100 text-emerald-800" };
    if (c.trial_ends_at && new Date(c.trial_ends_at) > now) return { label: "Trial", cls: "bg-amber-100 text-amber-800" };
    return { label: "Expired", cls: "bg-red-100 text-red-800" };
  };

  return (
    <main className="px-6 md:px-10 py-8" data-testid="platform-corporate-detail">
      <Link to="/platform/corporate" data-testid="corporate-detail-back" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4">
        <ArrowLeft className="h-4 w-4" /> All corporate accounts
      </Link>

      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2.5" data-testid="corporate-detail-name">
            <Crown className="h-6 w-6 text-amber-600" />
            {k.name}
            {k.deactivated && (
              <span className="rounded-full bg-slate-200 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-700" data-testid="corporate-detail-deactivated">
                Deactivated
              </span>
            )}
          </h1>
          <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
            <span>{k.billing_email}</span>
            <span className="font-mono">{k.id.slice(0, 8)}…</span>
            <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> Joined {new Date(k.created_at).toLocaleDateString()}</span>
            {k.paid_until && <span>Paid until {new Date(k.paid_until).toLocaleDateString()}</span>}
            {k.custom_monthly_price_usd > 0 && (
              <span className="inline-flex items-center gap-1 font-semibold text-slate-700">
                <DollarSign className="h-3 w-3" /> ${Number(k.custom_monthly_price_usd).toFixed(0)}/mo custom rate
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
        <button
          onClick={() => setEditOpen(true)}
          data-testid="corporate-edit-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 transition"
        >
          <Pencil className="h-4 w-4" /> Edit account
        </button>
        <button
          onClick={load}
          data-testid="platform-refresh-btn"
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </button>
        </div>
      </header>

      {editOpen && <EditCorporateDialog open onClose={() => setEditOpen(false)} corporate={k} onSaved={load} />}

      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="corporate-detail-counts">
        <CountCard Icon={Building2} label="Locations" value={data.locations.length} />
        <CountCard Icon={GraduationCap} label="Students" value={data.locations.reduce((a, l) => a + l.student_count, 0)} accent="blue" />
        <CountCard Icon={CreditCard} label="Payments" value={data.payments.length} />
        <CountCard Icon={DollarSign} label="Collected" value={`$${data.collected_usd.toFixed(2)}`} accent="emerald" />
      </section>

      <OwnerSubscriptionCard targetType="corporate" targetId={k.id} subscription={data.subscription} onChanged={load} />

      <section className="rounded-xl border border-slate-200 bg-white overflow-hidden mb-6" data-testid="corporate-detail-locations">
        <div className="px-5 py-4 border-b border-slate-200">
          <div className="font-display text-base font-semibold text-slate-900">Locations</div>
          <div className="text-xs text-slate-500">Every company under this corporate account. Click a name for full detail.</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-5 py-2.5">Location</th>
                <th className="px-5 py-2.5">Type</th>
                <th className="px-5 py-2.5">Status</th>
                <th className="px-5 py-2.5 text-right">Students</th>
                <th className="px-5 py-2.5 text-right">Employees</th>
                <th className="px-5 py-2.5">Joined</th>
              </tr>
            </thead>
            <tbody>
              {data.locations.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-500">No locations yet.</td></tr>
              )}
              {data.locations.map((l) => {
                const s = locStatus(l);
                const isSchool = (l.type || "business") === "school";
                return (
                  <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`corporate-location-${l.id}`}>
                    <td className="px-5 py-2.5">
                      <Link
                        to={`/platform/companies/${l.id}`}
                        data-testid={`corporate-location-link-${l.id}`}
                        className="font-medium text-slate-900 hover:text-primary hover:underline underline-offset-2"
                      >
                        {l.name}
                      </Link>
                    </td>
                    <td className="px-5 py-2.5">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${
                        isSchool ? "bg-blue-100 text-blue-800" : l.type === "care" ? "bg-emerald-100 text-emerald-800" : l.type === "daycare" ? "bg-pink-100 text-pink-800" : "bg-slate-100 text-slate-700"
                      }`}>
                        {isSchool ? <GraduationCap className="h-3 w-3" /> : l.type === "care" ? <HeartHandshake className="h-3 w-3" /> : l.type === "daycare" ? <Baby className="h-3 w-3" /> : <Briefcase className="h-3 w-3" />}
                        {isSchool ? "School" : l.type === "care" ? "Care home" : l.type === "daycare" ? "Daycare" : "Office"}
                      </span>
                    </td>
                    <td className="px-5 py-2.5">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${s.cls}`}>{s.label}</span>
                    </td>
                    <td className="px-5 py-2.5 text-right text-slate-700">{l.student_count}</td>
                    <td className="px-5 py-2.5 text-right text-slate-700">{l.employee_count}</td>
                    <td className="px-5 py-2.5 text-slate-700">{new Date(l.created_at).toLocaleDateString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="corporate-detail-payments">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <div className="font-display text-base font-semibold text-slate-900">Combined payment history</div>
            <div className="text-xs text-slate-500">Corporate-level checkouts plus every location's own payments.</div>
          </div>
          <div className="text-xs">
            <span className="font-semibold text-emerald-700">${data.collected_usd.toFixed(2)} collected</span>
            <span className="text-slate-500"> · {data.paid_count} paid</span>
          </div>
        </div>
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-5 py-2.5">Date</th>
                <th className="px-5 py-2.5">Account</th>
                <th className="px-5 py-2.5">Paid by</th>
                <th className="px-5 py-2.5">Plan</th>
                <th className="px-5 py-2.5 text-right">Amount</th>
                <th className="px-5 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.payments.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-500">No payments yet.</td></tr>
              )}
              {data.payments.map((p) => (
                <tr key={p.id} className="border-t border-slate-100" data-testid={`corporate-payment-${p.id}`}>
                  <td className="px-5 py-2.5 text-slate-700 whitespace-nowrap">{p.created_at ? new Date(p.created_at).toLocaleString() : "—"}</td>
                  <td className="px-5 py-2.5">
                    <div className="font-medium text-slate-900">{p.account_name}</div>
                    <div className="text-[11px] text-slate-400 uppercase">{p.target_type}</div>
                  </td>
                  <td className="px-5 py-2.5 text-slate-700">{p.user_email}</td>
                  <td className="px-5 py-2.5">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-slate-700">
                      {p.plan === "annual" ? "Annual" : "Monthly"}
                    </span>
                  </td>
                  <td className="px-5 py-2.5 text-right font-semibold text-slate-900">${p.amount.toFixed(2)}</td>
                  <td className="px-5 py-2.5">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${
                      p.payment_status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
                    }`}>
                      {p.payment_status === "paid" ? "Paid" : p.payment_status || "Pending"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}

function CountCard({ Icon, label, value, accent }) {
  const ring = { blue: "ring-blue-200 bg-blue-50", emerald: "ring-emerald-200 bg-emerald-50" }[accent] || "ring-slate-200 bg-white";
  return (
    <div className={`rounded-xl border border-slate-200 p-5 ring-1 ${ring}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <div className="mt-2 font-display text-3xl font-bold tracking-tight text-slate-900">{value}</div>
    </div>
  );
}
